// Clusters admin — group clinics into regional/business clusters.
import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { AdminHeader, AdminTable } from './AdminShared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Search, X, Building2 } from 'lucide-react';

const EMPTY = { name: '', description: '', manager_name: '', clinic_names: [], active: true };

export default function ClustersTab() {
  const [rows, setRows] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [clinicSearch, setClinicSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [c, cl] = await Promise.all([api.get('/clusters'), api.get('/clinics')]);
      setRows(c.data);
      setClinics(cl.data);
    } catch { toast.error('Failed to load clusters'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setClinicSearch(''); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({
      name: r.name || '',
      description: r.description || '',
      manager_name: r.manager_name || '',
      clinic_names: r.clinic_names || [],
      active: r.active !== false,
    });
    setClinicSearch('');
    setOpen(true);
  };
  const save = async () => {
    if (!form.name?.trim()) { toast.error('Cluster name is required'); return; }
    try {
      if (editing) await api.patch(`/clusters/${editing.id}`, form);
      else await api.post('/clusters', form);
      toast.success(editing ? 'Cluster updated' : 'Cluster created');
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed to save'); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete cluster "${r.name}"? Clinics inside it will become unassigned.`)) return;
    try { await api.delete(`/clusters/${r.id}`); toast.success('Deleted'); load(); }
    catch { toast.error('Delete failed'); }
  };

  const toggleClinic = (name) => {
    setForm(f => ({
      ...f,
      clinic_names: f.clinic_names.includes(name)
        ? f.clinic_names.filter(x => x !== name)
        : [...f.clinic_names, name],
    }));
  };

  const filteredClinics = useMemo(() => {
    const q = clinicSearch.trim().toLowerCase();
    return clinics.filter(c => !q || c.name?.toLowerCase().includes(q));
  }, [clinics, clinicSearch]);

  // Track which clinics are already claimed by OTHER clusters (informational)
  const claimedByOtherCluster = useMemo(() => {
    const map = new Map();
    rows.forEach(r => {
      if (editing && r.id === editing.id) return;
      (r.clinic_names || []).forEach(n => map.set(n, r.name));
    });
    return map;
  }, [rows, editing]);

  return (
    <div>
      <AdminHeader
        title="Clusters & Regions"
        description="Group clinics into business clusters (regions / territories). Powers the Cluster dashboard, and lets Regional Heads see roll-up numbers."
        onCreate={openCreate}
        createLabel="New Cluster"
        createTestId="admin-create-cluster"
      />
      <AdminTable
        loading={loading}
        rows={rows}
        rowTestId={(id) => `admin-cluster-row-${id}`}
        columns={[
          { key: 'name', label: 'Name', render: (r) => <span className="font-medium text-stone-900">{r.name}</span> },
          { key: 'clinic_names', label: 'Clinics', render: (r) => (
            <span className="text-xs text-stone-600">
              {(r.clinic_names || []).length === 0 ? (
                <span className="text-amber-700">No clinics assigned</span>
              ) : (
                <>
                  <strong className="text-stone-900">{r.clinic_names.length}</strong>{' '}
                  <span className="text-stone-500">
                    · {r.clinic_names.slice(0, 3).join(', ')}
                    {r.clinic_names.length > 3 ? ` +${r.clinic_names.length - 3} more` : ''}
                  </span>
                </>
              )}
            </span>
          )},
          { key: 'manager_name', label: 'Manager / Head' },
          { key: 'description', label: 'Description' },
        ]}
        onEdit={openEdit}
        onDelete={remove}
      />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Cluster' : 'New Cluster'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600">Cluster name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. North India"
                  className="h-9"
                  data-testid="cluster-field-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-600">Manager / Business Head</Label>
                <Input
                  value={form.manager_name}
                  onChange={(e) => setForm({ ...form, manager_name: e.target.value })}
                  placeholder="e.g. Rakesh Sharma"
                  className="h-9"
                  data-testid="cluster-field-manager"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-600">Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="resize-none text-sm"
                data-testid="cluster-field-description"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase tracking-wider text-stone-600">
                  Clinics <span className="text-stone-400">({form.clinic_names.length} of {clinics.length} selected)</span>
                </Label>
                {form.clinic_names.length > 0 && (
                  <button
                    onClick={() => setForm({ ...form, clinic_names: [] })}
                    className="text-[10px] text-stone-500 hover:text-stone-800 uppercase tracking-wider"
                    data-testid="cluster-clear-clinics"
                  >
                    <X className="w-3 h-3 inline mr-0.5" /> clear
                  </button>
                )}
              </div>
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-stone-400" />
                <Input
                  value={clinicSearch}
                  onChange={(e) => setClinicSearch(e.target.value)}
                  placeholder="Search clinics…"
                  className="h-8 pl-7 text-sm"
                  data-testid="cluster-clinic-search"
                />
              </div>
              <div className="border border-stone-200 rounded max-h-64 overflow-auto hc-scrollbar">
                {filteredClinics.length === 0 && (
                  <div className="p-3 text-xs text-stone-400 text-center">No clinics found.</div>
                )}
                {filteredClinics.map((c) => {
                  const checked = form.clinic_names.includes(c.name);
                  const claimed = claimedByOtherCluster.get(c.name);
                  return (
                    <label
                      key={c.id}
                      className={`flex items-center gap-2 px-3 py-1.5 border-b border-stone-100 last:border-b-0 hover:bg-stone-50 cursor-pointer text-sm ${checked ? 'bg-emerald-50' : ''}`}
                      data-testid={`cluster-clinic-option-${c.id}`}
                    >
                      <Checkbox checked={checked} onCheckedChange={() => toggleClinic(c.name)} />
                      <Building2 className="w-3.5 h-3.5 text-stone-400" />
                      <span className="flex-1 text-stone-800">{c.name}</span>
                      {claimed && !checked && (
                        <span className="text-[10px] text-amber-700 uppercase tracking-wider">
                          in {claimed}
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="cluster-save">
              {editing ? 'Save changes' : 'Create cluster'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
